/**
 * Spec backend-followups — hook that polls `/api/v1/tenants/:tenantId/budget`
 * for the active tenant on a 30-second cadence, mirroring the cadence used by
 * `useAdminCounts`.
 *
 * Usage:
 *
 *   const { budget, loading, error } = useTenantBudget();
 *
 * Resolution rules:
 *   - Tenant id is taken from `useBrand().selectedBrand?.tenant_id` first,
 *     falling back to `useBrand().activeTenantId` (which also covers the
 *     bootstrap-tenant flow used by settings/admin pages).
 *   - When no tenant id is resolvable yet, the hook stays in `loading: true`
 *     and `budget: null` so consumers can render nothing until the brand
 *     selection settles.
 *   - Polling stops on unmount and is restarted whenever the active tenant id
 *     changes (e.g., user switches brand to a different tenant).
 */
import { useEffect, useState } from 'react';
import { getTenantBudget } from '../api/tenantBudget.js';
import type { TenantBudget } from '../api/tenantBudget.js';
import { useBrand } from '../lib/brand-context.js';

const POLL_INTERVAL_MS = 30_000;

export interface UseTenantBudgetResult {
  budget: TenantBudget | null;
  loading: boolean;
  error: string | null;
}

export function useTenantBudget(): UseTenantBudgetResult {
  const { selectedBrand, activeTenantId } = useBrand();
  const tenantId = selectedBrand?.tenant_id ?? activeTenantId ?? null;

  const [budget, setBudget] = useState<TenantBudget | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!tenantId) {
      // No tenant resolvable yet — stay in loading state, don't render
      // meaningless "$0 of $0" copy in the consumer.
      setBudget(null);
      setLoading(true);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    const load = async (): Promise<void> => {
      try {
        const next = await getTenantBudget(tenantId);
        if (cancelled) return;
        setBudget(next);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    const id = window.setInterval(() => {
      void load();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [tenantId]);

  return { budget, loading, error };
}
